import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';
import { isAdminUser } from '../../../lib/admin';
import { logoutAndReturnHome } from '../../../lib/logout';

/**
 * `theme` controls the text color while the nav sits over the hero (before the
 * user scrolls and the bar turns solid white). 'light' — the default — keeps the
 * original dark text for pages with a light hero, so existing pages are
 * unchanged. Pass 'dark' on pages whose hero is dark (e.g. the WatchAI app page)
 * so the wordmark and links stay legible over it. Once scrolled, both themes use
 * dark text on the white bar.
 */
export default function Navigation({ theme = 'light' }: { theme?: 'light' | 'dark' }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setUser(data.session?.user ?? null));

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const displayName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email;
  const isAdmin = isAdminUser(user);

  // Over a dark hero (not yet scrolled), use light text; otherwise dark text.
  const overDark = theme === 'dark' && !isScrolled;
  const brandClass = overDark ? 'text-white' : 'text-gray-900';
  const linkClass = overDark
    ? 'text-gray-200 hover:text-white'
    : 'text-gray-700 hover:text-blue-600';

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3 cursor-pointer">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <i className="ri-phone-line text-white text-2xl"></i>
            </div>
            <span className={`text-2xl font-bold ${brandClass}`}>
              24/7 AI 360
            </span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/apps" className={`font-medium transition-colors cursor-pointer ${linkClass}`}>
              Apps
            </Link>
            <Link to="/pricing" className={`font-medium transition-colors cursor-pointer ${linkClass}`}>
              Pricing
            </Link>
            <Link to="/about" className={`font-medium transition-colors cursor-pointer ${linkClass}`}>
              About Us
            </Link>
            <Link to="/get-started" className={`font-medium transition-colors cursor-pointer ${linkClass}`}>
              Get Started
            </Link>
            {user ? (
              <div className="flex items-center gap-3">
                <Link
                  to={isAdmin ? '/backoffice' : '/'}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  {displayName || 'My Account'}
                </Link>
                <button
                  type="button"
                  onClick={logoutAndReturnHome}
                  className={`font-medium transition-colors cursor-pointer ${linkClass}`}
                >
                  Log out
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all whitespace-nowrap cursor-pointer"
              >
                Login / Sign Up
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
